import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CANDIDATE_APPLY_ROUTE } from '../constants';

interface FAQItemProps {
    question: string;
    answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div
            className="p-4 sm:px-4 sm:py-6"
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '10px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '14px',
                boxSizing: 'border-box'
            }}
        >
            <button
                className="flex justify-between items-center w-full text-left focus:outline-none group"
                onClick={() => setIsOpen(!isOpen)}
                style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
            >
                <span className="text-[14px] leading-[20px] sm:text-[18px] sm:leading-[25px] pr-4 flex-1" style={{
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    color: '#FFFFFF',
                    margin: 0
                }}>
                    {question}
                </span>
                <div className="flex items-center justify-center min-w-[44px] min-h-[44px] -mr-3 sm:mr-0">
                    <svg
                        className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="rgba(255, 255, 255, 0.5)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </div>
            </button>
            {isOpen && (
                <p className="text-[14px] leading-[21px] sm:text-[16px] sm:leading-[24px] pt-2" style={{
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.85)',
                    margin: 0
                }}>
                    {answer}
                </p>
            )}
        </div>
    );
};

interface FAQProps {
    candidateFaqs?: Array<{ q: string; a: string }>;
    employerFaqs?: Array<{ q: string; a: string }>;
}

const FAQ: React.FC<FAQProps> = ({ candidateFaqs: propCandidateFaqs, employerFaqs: propEmployerFaqs }) => {
    const navigate = useNavigate();

    // Default FAQs for Home page (shorter versions)
    const defaultCandidateFaqs = [
        { q: "Do I have to apply for jobs?", a: "No! David, your AI recruiter will share roles with you based on the preferences you set." },
        { q: "How does the bonus work?", a: "As a thank you for trusting us, we give you a $500 dollar sign on bonus out of our pocket. Additionally, if a friend signs up using your unique referral code we'll send you a $500 dollar thank you!" },
        { q: "What types of roles are available on Hush?", a: "We help companies hire for all types of roles! Finance, technology, operations, sales, our goal is to help everyone find their next dream role." }
    ];

    const defaultEmployerFaqs = [
        { q: "How is Hush different from traditional recruitment firms?", a: "Traditional agencies are expensive, commission-driven, and often push candidates for their own incentives. Hush eliminates the middleman. Our AI recruiter, David, connects you directly with top passive talent, handling matching, outreach, and scheduling—so you meet only qualified, genuinely interested candidates, with full transparency and no hidden agendas." },
        { q: "How do you ensure quality matches?", a: "Every candidate on Hush is vetted by our team before joining, ensuring only top professionals make it into the network. From there, our AI recruiter, David, tailors matches based on your feedback—constantly refining recommendations so you connect only with candidates who truly fit your needs." },
        { q: "What industries does Hush specialize in?", a: "Our core focus: finance, tech and startups." }
    ];

    const candidateFaqs = propCandidateFaqs || defaultCandidateFaqs;
    const employerFaqs = propEmployerFaqs || defaultEmployerFaqs;

    return (
        <section className="relative px-[22px] sm:px-4 overflow-x-hidden pt-2 pb-4 sm:pt-16 sm:pb-16">
            <div className="container mx-auto relative z-10 max-w-[1600px] sm:px-16">
                <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-24">
                    {/* Left Side - Mobile responsive */}
                    <div className="flex flex-col gap-8 lg:gap-14 w-full lg:w-[470px] lg:flex-shrink-0">
                        <h2 className="text-[32px] leading-[38px] sm:text-[48px] sm:leading-[56px] lg:text-[clamp(48px,6vw,96px)] lg:leading-[1.2]" style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            letterSpacing: '-0.05em',
                            color: '#FFFFFF',
                            margin: 0
                        }}>
                            Frequently Asked Questions
                        </h2>
                        <button
                            onClick={() => navigate(CANDIDATE_APPLY_ROUTE)}
                            className="flex items-center justify-center transition-all hover:scale-105 min-w-[160px] min-h-[48px] sm:w-[176px] sm:h-[67px]"
                            style={{
                                padding: '14px 24px',
                                background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
                                filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
                                backdropFilter: 'blur(50px)',
                                borderRadius: '10px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '16px',
                                lineHeight: '20px',
                                color: '#FFFFFF',
                                cursor: 'pointer'
                            }}
                        >
                            Try for free
                        </button>
                    </div>

                    {/* Right Side - Mobile responsive */}
                    <div className="flex flex-col gap-4 w-full lg:w-[646px] lg:flex-shrink-0">
                        {/* For Candidates Section - Only show if there are FAQs */}
                        {candidateFaqs.length > 0 && (
                            <>
                                <h3 style={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 500,
                                    fontSize: '18px',
                                    lineHeight: '25px',
                                    color: '#FFFFFF',
                                    margin: 0,
                                    marginBottom: '12px'
                                }}>
                                    For Candidates
                                </h3>
                                <div className="flex flex-col gap-4">
                                    {candidateFaqs.map(faq => <FAQItem key={faq.q} question={faq.q} answer={faq.a} />)}
                                </div>
                            </>
                        )}

                        {/* For Employers Section - Only show if there are FAQs */}
                        {employerFaqs.length > 0 && (
                            <>
                                <h3 style={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 500,
                                    fontSize: '18px',
                                    lineHeight: '25px',
                                    color: '#FFFFFF',
                                    margin: 0,
                                    marginTop: candidateFaqs.length > 0 ? '16px' : '0',
                                    marginBottom: '12px'
                                }}>
                                    For Employers
                                </h3>
                                <div className="flex flex-col gap-4">
                                    {employerFaqs.map(faq => <FAQItem key={faq.q} question={faq.q} answer={faq.a} />)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQ;
